import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Settings, Sun, Moon, LogOut, ChevronsUpDown } from 'lucide-react';
import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/useAuth';
import { useUIStore } from '@/store/uiStore';
import { menuGroups } from './navegacao';

const roleLabels: Record<string, string> = {
  super_admin: 'Super Administrador',
  admin: 'Administrador',
  viewer: 'Visualizador',
};

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setOpenMobile } = useSidebar();
  const { manager, logout, can } = useAuth();

  // Só mostra as áreas que o cargo do manager permite; oculta grupos vazios.
  const visibleGroups = menuGroups
    .map((g) => ({ ...g, items: g.items.filter((i) => can(i.permission)) }))
    .filter((g) => g.items.length > 0);
  const theme = useUIStore((s) => s.theme);
  const toggleTheme = useUIStore((s) => s.toggleTheme);

  const isDark =
    theme === 'dark' ||
    (theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  const name = manager?.name || 'Administrador';
  const roleLabel = manager?.role ? roleLabels[manager.role] : 'Admin';
  const avatar = `https://api.dicebear.com/7.x/avataaars/svg?seed=${name}`;

  const closeMobile = () => setOpenMobile(false);

  const handleLogout = async () => {
    if (confirm('Deseja realmente sair do painel?')) await logout();
  };

  return (
    <SidebarRoot collapsible="icon">
      {/* Marca */}
      <SidebarHeader>
        <Link
          to="/"
          onClick={closeMobile}
          className="flex items-center gap-2.5 rounded-lg px-1 py-1 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
        >
          <img src="/logo.png" alt="Curral Tech" className="size-9 shrink-0 object-contain" />
          <span className="flex min-w-0 flex-col leading-none group-data-[collapsible=icon]:hidden">
            <span className="truncate text-sm font-bold tracking-tight text-text-title">
              Curral Tech
            </span>
            <span className="truncate text-[11px] font-medium text-text-secondary">
              Painel Admin
            </span>
          </span>
        </Link>
      </SidebarHeader>

      {/* Navegação */}
      <SidebarContent>
        {visibleGroups.map((group, gi) => (
          <SidebarGroup key={gi}>
            {group.title && <SidebarGroupLabel>{group.title}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
                        <Link
                          to={item.path}
                          onClick={closeMobile}
                          aria-current={isActive ? 'page' : undefined}
                        >
                          <span
                            className={`flex size-7 shrink-0 items-center justify-center rounded-md transition-colors ${
                              isActive
                                ? 'bg-tertiary text-on-tertiary shadow-sm'
                                : 'text-text-secondary group-hover/btn:text-text-title'
                            }`}
                          >
                            <Icon size={18} />
                          </span>
                          <span
                            className={`truncate text-sm leading-none transition-colors group-data-[collapsible=icon]:hidden ${
                              isActive
                                ? 'font-semibold text-text-title'
                                : 'font-medium text-text-secondary group-hover/btn:text-text-title'
                            }`}
                          >
                            {item.label}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      {/* Card de usuário */}
      <SidebarFooter>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              aria-label="Abrir menu da conta"
              className="flex w-full items-center gap-2.5 rounded-lg p-1.5 text-left transition-colors hover:bg-sidebar-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring group-data-[collapsible=icon]:justify-center"
            >
              <img
                src={avatar}
                alt=""
                className="size-8 shrink-0 rounded-full bg-bg-elevated ring-1 ring-border"
              />
              <span className="flex min-w-0 flex-1 flex-col leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate text-sm font-semibold text-text-title">{name}</span>
                <span className="truncate text-xs text-text-secondary">{roleLabel}</span>
              </span>
              <ChevronsUpDown
                size={16}
                className="shrink-0 text-text-secondary group-data-[collapsible=icon]:hidden"
              />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              side="top"
              align="start"
              sideOffset={8}
              className="z-50 w-60 rounded-xl border border-border bg-bg-elevated p-1.5 shadow-elevated data-[state=open]:animate-fade-up"
            >
              <div className="flex items-center gap-3 px-2 py-2">
                <img
                  src={avatar}
                  alt=""
                  className="size-10 shrink-0 rounded-full bg-bg-main ring-1 ring-border"
                />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-text-title">{name}</p>
                  <p className="truncate text-xs text-text-secondary">{roleLabel}</p>
                </div>
              </div>

              <DropdownMenu.Separator className="my-1 h-px bg-border" />

              <DropdownMenu.Item
                onSelect={(e) => {
                  e.preventDefault();
                  toggleTheme();
                }}
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-sm text-text-body outline-none transition-colors data-[highlighted]:bg-muted data-[highlighted]:text-text-title"
              >
                {isDark ? <Sun size={18} /> : <Moon size={18} />}
                {isDark ? 'Tema claro' : 'Tema escuro'}
              </DropdownMenu.Item>

              <DropdownMenu.Item
                onSelect={() => navigate('/settings')}
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-sm text-text-body outline-none transition-colors data-[highlighted]:bg-muted data-[highlighted]:text-text-title"
              >
                <Settings size={18} />
                Configurações
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="my-1 h-px bg-border" />

              <DropdownMenu.Item
                onSelect={(e) => {
                  e.preventDefault();
                  handleLogout();
                }}
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-sm text-error outline-none transition-colors data-[highlighted]:bg-error/10"
              >
                <LogOut size={18} />
                Sair
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </SidebarFooter>

      <SidebarRail />
    </SidebarRoot>
  );
}
